<script setup lang="ts">
import type { UseProjectData } from '~/store-v2/projects/useProject'
import type { RegistrationUpdateByBiometryForm } from '~/store-v2/registration/useRegistrationContactChange'
import { SolButton, SolDivider } from '@solfacil/girassol'
import IconEdit from '~icons/material-symbols/edit-outline'
import { FEATURE_FLAGS } from '~/constants'
import { useFeatureFlag } from '~/store-v2/feature-flag/useFeatureFlag'
import { formatBrPhoneDisplay } from '~/utils/string'
import BiometryContactEditModal from './BiometryContactEditModal.vue'
import InverterCompatibilityCard from './InverterCompatibilityCard.vue'

const props = defineProps<{
  registration: any
  project: UseProjectData
  projectId: string
  isUpdateCustomerPending: boolean
  isComboConnect?: boolean
  orderId?: string | null
  confirmed?: boolean
  disabled?: boolean
  showConfirm?: boolean
  confirmLoading?: boolean
  confirmDisabled?: boolean
  showEditProject?: boolean
  editProjectLoading?: boolean
  editProjectDisabled?: boolean
  storeDisabled?: boolean
  orderError?: string
}>()

const emit = defineEmits<{
  (e: 'submitEdit', payload: RegistrationUpdateByBiometryForm): void
  (e: 'goToDueDate'): void
  (e: 'update:orderId', value: string): void
  (e: 'goToStore'): void
  (e: 'confirm'): void
  (e: 'editProject'): void
}>()

const { t, d } = useI18n()

const { data: enableDueDateChangeData } = useFeatureFlag<boolean>(FEATURE_FLAGS.ENABLE_DUE_DATE_CHANGE, false, true)
const enableDueDateChangeValue = computed(() => enableDueDateChangeData.value || false)
const shouldShowDueDateSection = computed(() =>
  enableDueDateChangeValue.value
  && props.project?.selected_installments?.due_day
  && props.project?.selected_installments?.first_due_date,
)

const statusTag = computed(() => props.confirmed
  ? { id: 'confirmation-status', text: t('customer.biometry.confirmation.status_approved'), variant: 'fill', size: 'medium', type: 'positive' } as const
  : { id: 'confirmation-status', text: t('customer.biometry.confirmation.status_in_progress'), variant: 'fill', size: 'medium', type: 'warning' } as const)

const legals = computed(() => props.registration?.company?.legal_representatives?.filter((legal: any) => legal.cpf !== props.registration?.company?.majority_partner.cpf))
const firstLegalRepresentant = computed(() => legals.value && legals.value[0])
const secondLegalRepresentant = computed(() => legals.value && legals.value[1])
const hasFirstLegalRepresentant = computed(() => legals.value && !!legals.value[0]?.cpf)
const hasSecondLegalRepresentant = computed(() => legals.value && !!legals.value[1]?.cpf)

const customerFields = computed(() => [
  { label: t('customer.biometry.modal_sending.form.email'), value: props.registration?.customer?.contact?.email || '-' },
  { label: t('customer.biometry.modal_sending.form.phone'), value: formatBrPhoneDisplay(props.registration?.customer?.contact?.phone || '') || '-' },
])
const guarantorFields = computed(() => [
  { label: t('customer.biometry.modal_sending.form.email_guarantor'), value: props.registration?.guarantor?.contact?.email || '-' },
  { label: t('customer.biometry.modal_sending.form.phone_guarantor'), value: formatBrPhoneDisplay(props.registration?.guarantor?.contact?.phone || '') || '-' },
])
const companyFields = computed(() => [
  { label: t('company.biometry.modal_sending.form.email'), value: props.registration?.company?.majority_partner?.contact?.email || '-' },
  { label: t('company.biometry.modal_sending.form.phone'), value: formatBrPhoneDisplay(props.registration?.company?.majority_partner?.contact?.phone || '') || '-' },
])
const firstLegalFields = computed(() => [
  { label: t('company.biometry.modal_sending.form.email'), value: firstLegalRepresentant.value?.contact?.email || '-' },
  { label: t('company.biometry.modal_sending.form.phone'), value: formatBrPhoneDisplay(firstLegalRepresentant.value?.contact?.phone || '') || '-' },
])
const secondLegalFields = computed(() => [
  { label: t('company.biometry.modal_sending.form.email'), value: secondLegalRepresentant.value?.contact?.email || '-' },
  { label: t('company.biometry.modal_sending.form.phone'), value: formatBrPhoneDisplay(secondLegalRepresentant.value?.contact?.phone || '') || '-' },
])

const contactCards = computed(() => {
  if (props.registration?.person_type === 'pf') {
    const cards = [{ title: t('customer.biometry.modal_sending.contact_info.title'), description: t('customer.biometry.modal_sending.contact_info.description'), fields: customerFields.value }]
    if (props.registration?.guarantor)
      cards.push({ title: t('customer.biometry.modal_sending.guarantor_info.title'), description: t('customer.biometry.modal_sending.guarantor_info.description'), fields: guarantorFields.value })
    return cards
  }
  const cards = [{ title: t('company.biometry.modal_sending.majority_info.title'), description: t('company.biometry.modal_sending.majority_info.description'), fields: companyFields.value }]
  if (hasFirstLegalRepresentant.value)
    cards.push({ title: t('company.biometry.modal_sending.first_legal_info.title'), description: t('company.biometry.modal_sending.first_legal_info.description'), fields: firstLegalFields.value })
  if (hasSecondLegalRepresentant.value)
    cards.push({ title: t('company.biometry.modal_sending.second_legal_info.title'), description: t('company.biometry.modal_sending.second_legal_info.description'), fields: secondLegalFields.value })
  return cards
})

const dueDateFields = computed(() => [
  { label: t('customer.biometry.modal_sending.due_day'), value: props.project?.selected_installments?.due_day || '-' },
  { label: t('customer.biometry.modal_sending.due_date.first_due_date'), value: props.project?.selected_installments?.first_due_date ? d(props.project?.selected_installments?.first_due_date) : '-' },
])

const infoCardWidth = computed(() => props.isComboConnect
  ? 'md:system:w-[272px] md:system:shrink-0'
  : 'md:system:flex-1')

const editModalOpen = defineModel<boolean>('editOpen', { default: false })

function handleOpenEditModal() {
  if (props.disabled)
    return

  editModalOpen.value = true
}

function handleSubmitEdit(payload: RegistrationUpdateByBiometryForm) {
  if (props.disabled || props.isUpdateCustomerPending)
    return

  emit('submitEdit', payload)
}

function handleGoToDueDate() {
  if (props.disabled)
    return

  emit('goToDueDate')
}

function handleOrderIdUpdate(value: string) {
  if (props.disabled)
    return

  emit('update:orderId', value)
}

function handleGoToStore() {
  if (props.storeDisabled)
    return

  emit('goToStore')
}

function handleConfirm() {
  if (props.confirmDisabled || props.confirmLoading)
    return

  emit('confirm')
}

function handleEditProject() {
  if (props.editProjectDisabled || props.editProjectLoading)
    return

  emit('editProject')
}

watch(() => props.disabled, (disabled) => {
  if (disabled)
    editModalOpen.value = false
})
</script>

<template>
  <div class="biometry-confirmation-cards w-full p-6 rounded-lg border border-neutral-high-medium">
    <div class="flex items-center gap-3">
      <span class="font-highlight text-neutral-low-dark text-[20px]">{{ t('customer.biometry.confirmation.title') }}</span>
      <SolTag
        :id="statusTag.id"
        :text="statusTag.text"
        :variant="statusTag.variant"
        :size="statusTag.size"
        :type="statusTag.type"
      />
    </div>
    <p class="fonts-body-medium-regular text-brand-primary-medium mt-3">
      {{ t('customer.biometry.confirmation.description') }}
    </p>

    <div class="flex flex-col md:system:flex-row gap-4 mt-8">
      <div
        v-for="(card, index) in contactCards"
        :key="`contact-${index}`"
        class="flex flex-col gap-2 p-6 rounded-md bg-neutral-high-light"
        :class="infoCardWidth"
      >
        <span class="font-highlight text-neutral-low-dark text-[16px] whitespace-nowrap">{{ card.title }}</span>
        <div class="flex flex-col gap-4">
          <span class="fonts-body-medium-regular text-brand-primary-medium">{{ card.description }}</span>
          <div class="flex flex-col gap-6">
            <div v-for="(field, fIndex) in card.fields" :key="fIndex" class="flex flex-col">
              <span class="fonts-body-small-regular text-neutral-low-light">{{ field.label }}</span>
              <span class="fonts-body-medium-bold text-neutral-low-pure break-words">{{ field.value }}</span>
            </div>
            <div>
              <SolButton :id="`edit-contact-${index}`" variant="secondary" size="small" :disabled="disabled" @click="handleOpenEditModal">
                <template #icon:left>
                  <IconEdit />
                </template>
                {{ t('customer.biometry.modal_sending.edit') }}
              </SolButton>
            </div>
          </div>
        </div>
      </div>

      <div v-if="shouldShowDueDateSection" class="flex flex-col gap-2 p-6 rounded-md bg-neutral-high-light" :class="infoCardWidth">
        <span class="font-highlight text-neutral-low-dark text-[16px] whitespace-nowrap">{{ t('customer.biometry.modal_sending.due_date.title') }}</span>
        <div class="flex flex-col gap-4">
          <span class="fonts-body-medium-regular text-brand-primary-medium">{{ t('customer.biometry.modal_sending.due_date.description') }}</span>
          <div class="flex flex-col gap-6">
            <div v-for="(field, fIndex) in dueDateFields" :key="fIndex" class="flex flex-col">
              <span class="fonts-body-small-regular text-neutral-low-light">{{ field.label }}</span>
              <span class="fonts-body-medium-bold text-neutral-low-pure">{{ field.value }}</span>
            </div>
            <div>
              <SolButton id="edit-due-date" variant="secondary" size="small" :disabled="disabled" @click="handleGoToDueDate">
                <template #icon:left>
                  <IconEdit />
                </template>
                {{ t('customer.biometry.modal_sending.edit') }}
              </SolButton>
            </div>
          </div>
        </div>
      </div>

      <InverterCompatibilityCard
        v-if="isComboConnect"
        class="md:system:flex-1 md:system:min-w-0"
        :project-id="projectId"
        :project="project"
        :order-id="orderId"
        :disabled="disabled"
        :store-disabled="storeDisabled"
        :error="orderError"
        @update:order-id="handleOrderIdUpdate"
        @go-to-store="handleGoToStore"
      />
    </div>

    <SolDivider thickness="x-small" orientation="horizontal" class="mt-8 mb-6" />

    <div class="flex flex-col md:system:flex-row justify-end gap-3">
      <SolButton
        v-if="showEditProject"
        id="edit_project_data"
        variant="secondary"
        size="medium"
        class="w-full md:system:w-auto"
        :loading="editProjectLoading"
        :disabled="editProjectDisabled || editProjectLoading"
        @click="handleEditProject"
      >
        {{ t('customer.biometry.footer.cancel') }}
      </SolButton>
      <SolButton
        v-if="showConfirm"
        id="confirm_and_advance"
        data-testid="btn-send-biometry"
        size="medium"
        class="w-full md:system:w-auto"
        :loading="confirmLoading"
        :disabled="confirmDisabled"
        @click="handleConfirm"
      >
        {{ t('customer.biometry.confirm_and_advance') }}
      </SolButton>
    </div>

    <BiometryContactEditModal
      :is-open="editModalOpen"
      :registration="registration"
      :project-id="projectId"
      :is-pending="isUpdateCustomerPending"
      @close="editModalOpen = false"
      @submit-edit="handleSubmitEdit"
    />
  </div>
</template>
